/**
 * A next step under a "not found" answer, in place of the old notice («Sin
 * coincidencias… Intenta reformular»), which told the reader to rephrase
 * without saying how.
 *
 * What it offers comes from refusal-help.ts: open the book the reader wanted to
 * read, say plainly when that book is not in the library yet, or give a
 * concrete way to rephrase. It renders only under a refusal, so an ordinary
 * answer, and the embedded chat, carry nothing extra.
 *
 * Question chips SUBMIT, like the follow-up chips. The library link opens in a
 * new tab when embedded, like the source cards: navigating inside the host
 * page's frame would strand the reader there.
 */
import { useMemo } from "react";
import type { Language, Strings } from "../strings";
import { detectBookIntents, inlineBookName, looksLikeTask, planReadingLink, planRefusalHelp } from "../chat/refusal-help";
import { useLibraryIndex } from "../chat/use-library-index";
import { corpusPath, softNavigate } from "../corpus/use-corpus-route";
import { isEmbedded } from "../embed/embed-mode";

function LibraryLink(props: { href: string; label: string }): JSX.Element {
  const embedded = isEmbedded();
  return (
    <a
      className="refusal-help__open"
      href={props.href}
      data-testid="refusal-help-open"
      {...(embedded ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      onClick={(event) => {
        if (embedded) return;
        event.preventDefault();
        softNavigate(props.href);
      }}
    >
      <svg className="refusal-help__open-icon" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path
          d="M8 3.6C6.6 2.7 4.7 2.4 2.5 2.6v9.6c2.2-.2 4.1.1 5.5 1m0-9.6c1.4-.9 3.3-1.2 5.5-1v9.6c-2.2-.2-4.1.1-5.5 1m0-9.6v9.6"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      {props.label}
    </a>
  );
}

/**
 * Under an ANSWERED question that was a request to read a book: one line into
 * the library (planReadingLink). Nothing else; the answer is already there.
 */
export function ReadingLink(props: { query: string; strings: Strings }): JSX.Element | null {
  const { query, strings } = props;
  const reading = useMemo(() => detectBookIntents(query).some((i) => i.reading), [query]);
  const library = useLibraryIndex(reading);
  const link = useMemo(() => (reading ? planReadingLink(query, library.index) : null), [reading, query, library.index]);
  if (link === null) return null;
  const label = link.chapter === null
    ? strings.readingLinkBook(link.name)
    : strings.readingLinkChapter(`${inlineBookName(link.name)} ${link.chapter}`);
  return (
    <p className="reading-link fade-in" data-testid="reading-link">
      <LibraryLink href={corpusPath(link.slug, link.open)} label={label} />
    </p>
  );
}

export function RefusalHelp(props: {
  query: string;
  language: Language;
  strings: Strings;
  onAsk?: (question: string) => void;
}): JSX.Element | null {
  const { query, language, strings, onAsk } = props;
  // The book list is needed only when a book is named; load it only then.
  const named = useMemo(() => !looksLikeTask(query) && detectBookIntents(query).length > 0, [query]);
  const library = useLibraryIndex(named);
  const help = useMemo(
    () => planRefusalHelp(query, library.index, language, (passage) => strings.relatedQuestion(null, passage)),
    [query, library.index, language, strings],
  );
  // A beat while the book list loads, rather than the general help flashing
  // first and then being replaced.
  if (named && !library.settled) return null;

  let lead: string;
  let link: { href: string; label: string } | null = null;
  let questions: string[] = [];
  switch (help.kind) {
    case "book": {
      const passage = help.chapter === null ? help.name : `${inlineBookName(help.name)} ${help.chapter}`;
      lead = help.reading ? strings.refusalBookLead(passage) : strings.refusalChapterLead;
      link = { href: corpusPath(help.slug, help.open), label: strings.refusalOpenBook(passage) };
      questions = help.questions;
      break;
    }
    case "chapter_missing":
      lead = strings.refusalChapterMissing(`${inlineBookName(help.name)} ${help.chapter}`);
      link = { href: corpusPath(help.slug, help.open), label: strings.refusalOpenBook(help.name) };
      break;
    case "missing":
      lead = help.homonym === null
        ? strings.refusalBookMissing(help.name)
        : `${strings.refusalBookMissing(help.name)} ${strings.refusalHomonym(help.homonym.name)}`;
      link = help.homonym === null
        ? { href: corpusPath(), label: strings.refusalBrowseLibrary }
        : { href: corpusPath(help.homonym.slug, help.homonym.open), label: strings.refusalOpenBook(help.homonym.name) };
      break;
    case "task":
      lead = strings.refusalTaskLead;
      link = { href: corpusPath(), label: strings.refusalBrowseLibrary };
      break;
    case "general":
      lead = strings.refusalGeneralLead;
      link = { href: corpusPath(), label: strings.refusalBrowseLibrary };
      break;
  }
  const chips = onAsk !== undefined ? questions : [];
  // Reading intent: the library is the answer, so it leads. A question about a
  // chapter: asking about the whole chapter leads, the library follows.
  const linkFirst = help.kind === "book" && help.reading;

  return (
    <aside className="refusal-help fade-in" aria-label={strings.refusalHelpLabel} data-testid="refusal-help" data-kind={help.kind}>
      <p className="refusal-help__lead">{lead}</p>
      {linkFirst && link !== null ? <LibraryLink {...link} /> : null}
      {chips.length > 0 ? (
        <div className="related__chips">
          {chips.map((question) => (
            <button
              key={question}
              type="button"
              className="related__chip refusal-help__chip"
              onClick={() => onAsk?.(question)}
            >
              <span className="related__chip-text">{question}</span>
            </button>
          ))}
        </div>
      ) : null}
      {!linkFirst && link !== null ? <LibraryLink {...link} /> : null}
    </aside>
  );
}
